import React from "react"
import styled from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezierBackgroundColor } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import {
  smallcapsBold13Styles,
  smallcapsBold14Styles,
  body14Styles,
} from "@constants/typography"
import { Pencil } from "@components/icons"
import BackgroundImageHelper, {
  Container as BackgroundImageHelperContainer,
} from "@components/BackgroundImageHelper"
import { disabledProps } from "@components/buttons/Button"
import { DesktopOnly, MobileAndTabletContainer } from "@utils/MediaQuery"

const FullWidthMobileAndTabletContainer = styled(MobileAndTabletContainer)`
  width: 100%;
`

const Container = styled.div`
  align-items: center;
  justify-content: space-between;
  display: flex;
  width: 100%;
  padding: 0 8px 0 4px;

  @media ${breakpoints.tablet} {
    padding: 0 calc(1 / 12 * 100vw);
  }

  @media ${breakpoints.laptop} {
    flex-grow: 1;
    justify-content: center;
    max-width: calc(7 / 12 * 100vw);
    margin-left: calc(2 / 12 * 100vw);
  }
`

const Product = styled.div`
  ${smallcapsBold14Styles}
  flex-shrink: 1;
  min-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const DeviceButton = styled.button`
  flex-shrink: 0;
  align-items: center;
  display: flex;
  width: calc(100% - 132px);
  height: 40px;
  padding: 0 12px 0 16px;
  background-color: transparent;
  border-radius: 2px;
  border: none;
  pointer-events: all;
  &:focus {
    outline: none;
  }

  @media ${breakpoints.tablet} {
    width: calc(100% - 148px);
  }

  @media ${breakpoints.laptop} {
    width: initial;
    min-width: 0;
    margin: 0 15px 0 27px;
    background-color: ${colors.dividerLineGray};

    &:hover {
      cursor: pointer;
    }
  }
`

const Device = styled.span`
  ${body14Styles}
  max-width: 192px;
  margin-left: 8px;
  padding-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${colors.mediumDarkGray};
`

const baseSwatchStyles = `
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-right: 12px;
  border: transparent;
  border-radius: 50%;
  overflow: hidden;
  &:active, &:focus {
    outline: none;
  }
`

export const ImageSwatch = styled.div`
  ${baseSwatchStyles}
  margin: 0;
  padding: 0;
  background-color: transparent;

  ${BackgroundImageHelperContainer} {
    ${baseSwatchStyles}
    width: 20px;
    height: 20px;
    padding: 0;
    overflow: hidden;
    background-color: transparent;
  }
`

export const ColorSwatch = styled.div<{ hexString?: string }>`
  ${baseSwatchStyles}
  background-color: #${({ hexString }) => hexString || "000000"};
  ${({ hexString }) =>
    `#${hexString}` === colors.white
      ? `border: 1px solid ${colors.borderGray};`
      : ""}
`

const IconWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  width: 20px;
  height: 20px;
  margin-left: 12px;

  @media ${breakpoints.tablet} {
    margin-left: 8px;
  }

  @media ${breakpoints.laptop} {
    margin-left: 8px;
  }
`

const ActionButton = styled.button`
  ${smallcapsBold13Styles}
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 132px;
  height: 36px;
  padding: 3px 0 0;
  border: none;
  border-radius: 2px;
  background-color: ${colors.black};
  transition: background-color ${animation.medium} ${cubicBezierBackgroundColor};
  pointer-events: all;
  cursor: pointer;
  color: ${colors.offWhite};
  &:focus {
    outline: none;
  }
  &:active {
    background-color: ${colors.black};
  }

  @media ${breakpoints.laptop} {
    ${smallcapsBold14Styles}
    width: initial;
    height: 40px;
    padding: 4px 30px 0 22px;
    color: ${colors.offWhite};
    &:hover {
      background-color: ${colors.hoverBlack};
    }
  }

  ${({ disabled }) => (disabled ? disabledProps : "")}
`

const Price = styled.span`
  margin-left: 8px;
  color: ${colors.mediumGray};
`

export interface ProductDetailsHeaderProps {
  notAvailableInCountry: boolean
  notAvailable?: boolean
  notAvailableButtonText?: string
  outOfStockButtonText?: string
  isAvailable?: boolean
  productPrice: string
  productName: string
  deviceName: string
  color: {
    name: string
    hexString?: string
    swatchImage?: string
  }
  onClickDevice?: () => void
  onClickAddToCart?: () => void
}

const ProductDetailsHeader: React.FC<ProductDetailsHeaderProps> = ({
  notAvailableInCountry,
  notAvailable,
  notAvailableButtonText,
  outOfStockButtonText,
  isAvailable,
  productPrice,
  productName,
  deviceName,
  color,
  onClickDevice,
  onClickAddToCart,
}: ProductDetailsHeaderProps) => {
  return (
    <>
      <FullWidthMobileAndTabletContainer>
        <Container>
          <DeviceButton onClick={onClickDevice}>
            {color.swatchImage ? (
              <ImageSwatch title={color.name} {...color}>
                <BackgroundImageHelper src={color.swatchImage} />
              </ImageSwatch>
            ) : (
              <ColorSwatch title={color.name} hexString={color.hexString} />
            )}
            <Device>{deviceName}</Device>
            <IconWrapper>
              <Pencil strokeColor={colors.black} />
            </IconWrapper>
          </DeviceButton>
          {notAvailableInCountry ? (
            <ActionButton className="notAvailable" disabled>
              Not Available
            </ActionButton>
          ) : notAvailable ? (
            <ActionButton className="notAvailable" disabled>
              {notAvailableButtonText || "Not Available"}
            </ActionButton>
          ) : (
            <ActionButton
              className="addToCart"
              onClick={onClickAddToCart}
              disabled={!isAvailable}
            >
              {isAvailable
                ? "Add to Cart"
                : outOfStockButtonText
                ? outOfStockButtonText
                : "Out of Stock"}
            </ActionButton>
          )}
        </Container>
      </FullWidthMobileAndTabletContainer>
      <DesktopOnly>
        <Container>
          <Product>{productName}</Product>
          <DeviceButton onClick={onClickDevice}>
            {color.swatchImage ? (
              <ImageSwatch title={color.name} {...color}>
                <BackgroundImageHelper src={color.swatchImage} />
              </ImageSwatch>
            ) : (
              <ColorSwatch title={color.name} hexString={color.hexString} />
            )}
            <Device>{deviceName}</Device>
            <IconWrapper>
              <Pencil strokeColor={colors.black} />
            </IconWrapper>
          </DeviceButton>
          {notAvailableInCountry ? (
            <ActionButton className="notAvailable" disabled>
              Not Available
            </ActionButton>
          ) : notAvailable ? (
            <ActionButton className="notAvailable" disabled>
              {notAvailableButtonText || "Not Available"}
            </ActionButton>
          ) : (
            <ActionButton
              className="addToCart"
              onClick={onClickAddToCart}
              disabled={!isAvailable}
            >
              {isAvailable
                ? "Add to Cart"
                : outOfStockButtonText
                ? outOfStockButtonText
                : "Out of Stock"}
              <Price>- ${productPrice}</Price>
            </ActionButton>
          )}
        </Container>
      </DesktopOnly>
    </>
  )
}

export default ProductDetailsHeader
